import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Building2, Plus, Edit2, Trash2, Eye, EyeOff, DollarSign, Percent, AlertCircle } from 'lucide-react';

interface BankingInfo {
  id: string;
  employee_id: string;
  account_holder_name: string;
  routing_number: string;
  account_number: string;
  account_type: 'checking' | 'savings';
  allocation_type: 'dollar' | 'percentage';
  allocation_value: number;
  priority: number;
  account_label: string;
  is_active: boolean;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

export default function EmployeeBanking() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [bankingInfo, setBankingInfo] = useState<BankingInfo[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAccountNumbers, setShowAccountNumbers] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    account_type: 'checking' as 'checking' | 'savings',
    allocation_type: 'percentage' as 'dollar' | 'percentage',
    allocation_value: 100,
    account_label: '',
  });

  useEffect(() => {
    loadEmployees();
    loadBankingInfo();
  }, []);

  const loadEmployees = async () => {
    const { data, error } = await supabase
      .from('employees')
      .select('id, first_name, last_name')
      .eq('is_active', true)
      .order('last_name');

    if (error) {
      console.error('Error loading employees:', error);
      return;
    }

    setEmployees(data || []);
  };

  const loadBankingInfo = async () => {
    const { data, error } = await supabase
      .from('employee_banking')
      .select('*')
      .eq('is_active', true)
      .order('priority');

    if (error) {
      console.error('Error loading banking info:', error);
      return;
    }

    setBankingInfo(data || []);
  };

  const getEmployeeAccounts = (employeeId: string) => {
    return bankingInfo.filter(b => b.employee_id === employeeId);
  };

  const validateAllocations = (employeeId: string, currentAccountId?: string): { valid: boolean; message?: string } => {
    const accounts = getEmployeeAccounts(employeeId).filter(a => a.id !== currentAccountId);

    const percentageAccounts = accounts.filter(a => a.allocation_type === 'percentage');
    const totalPercentage = percentageAccounts.reduce((sum, a) => sum + Number(a.allocation_value), 0);

    if (formData.allocation_type === 'percentage') {
      const newTotal = totalPercentage + Number(formData.allocation_value);
      if (newTotal > 100) {
        return { valid: false, message: `Total percentage would be ${newTotal}%. Cannot exceed 100%.` };
      }
    }

    return { valid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee) return;

    const validation = validateAllocations(selectedEmployee, editingId || undefined);
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    setLoading(true);

    try {
      const accounts = getEmployeeAccounts(selectedEmployee);
      const nextPriority = editingId
        ? accounts.find(a => a.id === editingId)?.priority || 1
        : Math.max(...accounts.map(a => a.priority), 0) + 1;

      const dataToSave = {
        employee_id: selectedEmployee,
        account_holder_name: formData.account_holder_name,
        routing_number: formData.routing_number,
        account_number: formData.account_number,
        account_type: formData.account_type,
        allocation_type: formData.allocation_type,
        allocation_value: formData.allocation_value,
        priority: nextPriority,
        account_label: formData.account_label,
        is_active: true,
      };

      if (editingId) {
        const { error } = await supabase
          .from('employee_banking')
          .update(dataToSave)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('employee_banking')
          .insert(dataToSave);

        if (error) throw error;
      }

      await loadBankingInfo();
      setShowForm(false);
      setEditingId(null);
      resetForm();
    } catch (error) {
      console.error('Error saving banking info:', error);
      alert('Failed to save banking information');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account: BankingInfo) => {
    setEditingId(account.id);
    setSelectedEmployee(account.employee_id);
    setFormData({
      account_holder_name: account.account_holder_name,
      routing_number: account.routing_number,
      account_number: account.account_number,
      account_type: account.account_type,
      allocation_type: account.allocation_type,
      allocation_value: account.allocation_value,
      account_label: account.account_label,
    });
    setShowForm(true);
  };

  const handleDelete = async (account: BankingInfo) => {
    if (!confirm('Are you sure you want to remove this banking account?')) return;

    const { error } = await supabase
      .from('employee_banking')
      .delete()
      .eq('id', account.id);

    if (error) {
      console.error('Error deleting banking info:', error);
      alert('Failed to delete banking information');
      return;
    }

    await loadBankingInfo();
  };

  const resetForm = () => {
    setFormData({
      account_holder_name: '',
      routing_number: '',
      account_number: '',
      account_type: 'checking',
      allocation_type: 'percentage',
      allocation_value: 100,
      account_label: '',
    });
    setSelectedEmployee('');
  };

  const maskAccountNumber = (accountNumber: string, show: boolean) => {
    if (show) return accountNumber;
    const visible = accountNumber.slice(-4);
    return `****${visible}`;
  };

  const toggleShowAccount = (accountId: string) => {
    setShowAccountNumbers(prev => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  const getEmployeeAllocationSummary = (employeeId: string) => {
    const accounts = getEmployeeAccounts(employeeId);
    const percentageSum = accounts
      .filter(a => a.allocation_type === 'percentage')
      .reduce((sum, a) => sum + Number(a.allocation_value), 0);

    const dollarSum = accounts
      .filter(a => a.allocation_type === 'dollar')
      .reduce((sum, a) => sum + Number(a.allocation_value), 0);

    return { percentageSum, dollarSum, accountCount: accounts.length };
  };

  const groupedByEmployee = employees.map(emp => ({
    employee: emp,
    accounts: getEmployeeAccounts(emp.id),
    summary: getEmployeeAllocationSummary(emp.id),
  })).filter(g => g.accounts.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-emerald-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Employee Banking</h2>
            <p className="text-sm text-gray-600">Manage ACH direct deposit accounts and split allocations</p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => {
              resetForm();
              setEditingId(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Account
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit' : 'Add'} Bank Account
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee
              </label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                required
                disabled={!!editingId}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
              >
                <option value="">Select an employee</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Label (Optional)
              </label>
              <input
                type="text"
                value={formData.account_label}
                onChange={(e) => setFormData({ ...formData, account_label: e.target.value })}
                placeholder="e.g., Primary Checking, Savings Account"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Holder Name
              </label>
              <input
                type="text"
                value={formData.account_holder_name}
                onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Routing Number
                </label>
                <input
                  type="text"
                  value={formData.routing_number}
                  onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                  required
                  pattern="[0-9]{9}"
                  maxLength={9}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="9 digits"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Type
              </label>
              <select
                value={formData.account_type}
                onChange={(e) => setFormData({ ...formData, account_type: e.target.value as 'checking' | 'savings' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>

            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Allocation Method
              </label>
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="allocation_type"
                    value="percentage"
                    checked={formData.allocation_type === 'percentage'}
                    onChange={(e) => setFormData({
                      ...formData,
                      allocation_type: e.target.value as 'percentage',
                      allocation_value: 100
                    })}
                    className="w-4 h-4 text-emerald-600"
                  />
                  <Percent className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Percentage</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="allocation_type"
                    value="dollar"
                    checked={formData.allocation_type === 'dollar'}
                    onChange={(e) => setFormData({
                      ...formData,
                      allocation_type: e.target.value as 'dollar',
                      allocation_value: 0
                    })}
                    className="w-4 h-4 text-emerald-600"
                  />
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Fixed Dollar Amount</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.allocation_type === 'percentage' ? 'Percentage (%)' : 'Dollar Amount ($)'}
                </label>
                <input
                  type="number"
                  value={formData.allocation_value}
                  onChange={(e) => setFormData({ ...formData, allocation_value: Number(e.target.value) })}
                  required
                  min="0"
                  max={formData.allocation_type === 'percentage' ? '100' : undefined}
                  step={formData.allocation_type === 'percentage' ? '0.01' : '0.01'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.allocation_type === 'percentage'
                    ? 'Percentage of net pay to deposit to this account'
                    : 'Fixed amount to deposit to this account (remainder goes to other accounts)'}
                </p>
              </div>
            </div>

            {selectedEmployee && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    {(() => {
                      const summary = getEmployeeAllocationSummary(selectedEmployee);
                      const validation = validateAllocations(selectedEmployee, editingId || undefined);

                      if (!validation.valid) {
                        return <p className="text-red-600 font-medium">{validation.message}</p>;
                      }

                      if (summary.accountCount === 0) {
                        return <p>This will be the first account for this employee.</p>;
                      }

                      return (
                        <div>
                          <p className="font-medium mb-1">Current allocations:</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            {summary.percentageSum > 0 && (
                              <li>{summary.percentageSum}% allocated by percentage</li>
                            )}
                            {summary.dollarSum > 0 && (
                              <li>${summary.dollarSum.toFixed(2)} allocated by dollar amount</li>
                            )}
                            <li>{summary.accountCount} account(s) configured</li>
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Holder</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Routing</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allocation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {groupedByEmployee.map(({ employee, accounts, summary }) => (
                accounts.map((account, index) => (
                  <tr key={account.id} className={index === 0 ? 'border-t-2 border-gray-300' : ''}>
                    {index === 0 && (
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" rowSpan={accounts.length}>
                        <div>
                          <div>{employee.first_name} {employee.last_name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {summary.accountCount} account{summary.accountCount > 1 ? 's' : ''}
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {account.account_label || <span className="text-gray-400 italic">No label</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {account.account_holder_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                      {account.routing_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                      <div className="flex items-center gap-2">
                        {maskAccountNumber(account.account_number, showAccountNumbers[account.id] || false)}
                        <button
                          onClick={() => toggleShowAccount(account.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showAccountNumbers[account.id] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                      {account.account_type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-1">
                        {account.allocation_type === 'percentage' ? (
                          <>
                            <Percent className="w-3 h-3 text-gray-400" />
                            <span>{account.allocation_value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-3 h-3 text-gray-400" />
                            <span>${Number(account.allocation_value).toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      #{account.priority}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(account)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(account)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ))}
              {groupedByEmployee.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                    No banking information configured. Click "Add Account" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">Split Direct Deposit Information:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Employees can have multiple bank accounts</li>
              <li>Use percentage allocation to split by percentage of net pay</li>
              <li>Use dollar allocation for fixed amounts (processed first, remainder goes to percentage accounts)</li>
              <li>Priority determines processing order for accounts with the same allocation type</li>
              <li>Total percentage allocations cannot exceed 100%</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Security Note:</strong> Banking information is stored securely. In production, consider implementing
          additional encryption at the application level for sensitive data.
        </p>
      </div>
    </div>
  );
}
